import React from 'react';
import {t} from '@lingui/core/macro';

import ProblemMonitor from 'shared/ui/icons/ProblemMonitor';
import SuccessfulMonitor from 'shared/ui/icons/SuccessfulMonitor';
import LoaderCentered from 'shared/ui/LoaderCentered/LoaderCentered';
import MainModal from 'shared/ui/Modal/MainModal';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import {getFormattedDate, secondsToFormattedString} from 'shared/lib/datetime';
import {getPortDesiredStateLabel} from 'features/port/lib/portMonitorHelper';
import usePortIncident from 'features/port/model/usePortIncident';

import PortCheckResultsTable from '../PortCheckResultsTable/PortCheckResultsTable';
import PortIncidentStatusBadge from '../PortIncidentStatusBadge/PortIncidentStatusBadge';

import styles from './PortIncidentModal.module.scss';

interface IPortIncidentModalProps {
    uuid: string,
    monitorName?: string,
    isOpen: boolean,
    handleClose: () => void,
    tzString?: string | null,
}

const PortIncidentModal: React.FC<IPortIncidentModalProps> = ({uuid, monitorName, isOpen, handleClose, tzString}) => {
    const {incident, isLoading, isIncidentOpen, durationSeconds, hasRecovery, dateFormat} =
        usePortIncident(uuid, isOpen);

    return (
        <MainModal
            dialogClassName={isIncidentOpen ? styles.backgroundModal : undefined}
            headerClassName={styles.header}
            header={
                <div>
                    <div className={styles.contentHeader}>
                        {t`Инцидент порта`}
                        {monitorName && <span className={styles.title}> - {monitorName}</span>}
                    </div>
                    {incident && (
                        <div className={styles.contentInfo}>
                            {monitorName && (
                                <div className={styles.monitorNameMobile}>
                                    <span>{t`Порт`}</span>
                                    <span>{monitorName}</span>
                                </div>
                            )}
                            <div className={styles.itemInfo}>
                                <span className={styles.title}>{t`Статус`}</span>
                                <div className={styles.containerStatus}>
                                    {isIncidentOpen ? <ProblemMonitor/> : <SuccessfulMonitor/>}
                                    <PortIncidentStatusBadge isOpen={isIncidentOpen}/>
                                </div>
                            </div>
                            <div className={styles.itemInfo}>
                                <span className={styles.title}>{t`Желаемое состояние`}</span>
                                <div className={styles.data}>
                                    {getPortDesiredStateLabel(incident.desired_state)}
                                </div>
                            </div>
                            <div className={styles.itemInfo}>
                                <span className={styles.title}>{t`Начало`}</span>
                                <div className={styles.data}>
                                    {getFormattedDate(incident.created_at, {format: dateFormat, tzString})}
                                </div>
                            </div>
                            <div className={styles.itemInfo}>
                                <span className={styles.title}>{t`Окончание`}</span>
                                <div className={styles.data}>
                                    {getFormattedDate(incident.resolved_at, {format: dateFormat, tzString})}
                                </div>
                            </div>
                            <div className={styles.itemInfo}>
                                <span className={styles.title}>{t`Длительность`}</span>
                                <div className={styles.data}>
                                    {secondsToFormattedString(durationSeconds)}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            }
            open={isOpen}
            handleClose={handleClose}
            size="md"
        >
            <ModalBodyWrapper>
                <LoaderCentered active={isLoading}/>

                {incident && (
                    <>
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>{t`При открытии`}</div>
                            <PortCheckResultsTable
                                results={incident.locations ?? []}
                                emptyText={t`Результаты проверки не сохранены`}
                            />
                        </div>

                        {hasRecovery && (
                            <div className={styles.section}>
                                <div className={styles.sectionTitle}>{t`При восстановлении`}</div>
                                <PortCheckResultsTable
                                    results={incident.recovery_locations}
                                    emptyText={t`Результаты проверки не сохранены`}
                                />
                            </div>
                        )}
                    </>
                )}
            </ModalBodyWrapper>
        </MainModal>
    );
};

export default PortIncidentModal;
